import React, { useState, useEffect, useCallback } from "react";
import { Card, CardBody, CardHeader, Button, Spinner, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Alert, Table,} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import Flatpickr from "react-flatpickr";
import CommentEditor from "../../components/Contract/CommentEditor";
import { EditorState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import "flatpickr/dist/themes/material_green.css"; // Flatpickr styles

const toProperCase = (word) =>
  typeof word === "string" || typeof word === "number"
    ? word.toString().charAt(0).toUpperCase() + word.toString().slice(1).toLowerCase()
    : "";

const Contracts = ({ isHome = false }) => {
  const { type } = useParams();
  const documentType = isHome ? "contract" : type;
  console.log(documentType);
  const navigate = useNavigate();
  const token = sessionStorage.getItem("authToken");

  // State management
  const [modal, setModal] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [contractsPerPage] = useState(10);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [contractToEdit, setContractToEdit] = useState(null);
  const [userData, setUserData] = useState(null);
  const [refreshData, setRefreshData] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const hiddenFields = [
    "_id",
    "addendums",
    "__v",
    "file",
    "created_by",
    "createdAt",
    "updatedAt",
  ];

  const statusOptions = ["active", "terminated", "expired"];
  const countryOptions = ['Kenya', 'Tanzania', 'Uganda', 'Nigeria', 'Mozambique', 'South Africa', 'Lesotho', 'Ethiopia', 'DRC Congo', 'Congo', 'Cameroon', 'Multiple'];
  const documentOptions = ["contract", "nda", "mou", "permit", "compliance", "license", "others"];

  // Modal toggles
  const toggleModal = () => setModal(!modal);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);
  const toggleEditModal = () => setEditModal(!editModal);

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return isNaN(date) ? dateString : format(date, "PPP"); // Example: "Mar 5, 2025"
  };

  // Fetch contracts from the API
  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      const url = isHome ? '' : `${documentType}`;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/contracts/${url}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Handle expired or unauthorized tokens
        if (response.status === 400) {
          sessionStorage.clear();
          navigate("/login");
          return;
        } 
        setError(`Error Fetching ${documentType} ${response.err || ''}`);
        throw new Error(`Request error ${response.err || ''}`);
      }

      // Sort contracts by creation date in descending order (newest first)
      data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setContracts(data);
    } catch (error) {
      console.error(`Error fetching ${documentType}:`, error);
      setError(`Error fetching ${documentType}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [documentType, isHome, navigate, token]);

  // Fetch contracts on component mount and when refreshData changes
  useEffect(() => {
    fetchContracts();
  }, [fetchContracts, refreshData]);
  
  // Update the user data
  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("authUser"));
    setUserData(user);
  }, []);

  // Show the error and success alerts for 5secs.
  useEffect(() => {
    if (!error && !success) return;
    
    const timer = setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 5000);
  
    return () => clearTimeout(timer);
  }, [error, success]);

  // Handle comment editor changes
  const handleEditorChange = (state) => {
    setEditorState(state);
  };

  // Redirect to the view page
  const confirmView = (contractId) => {
    navigate(`/contracts/${documentType}/view/${contractId}?h=${isHome}`);
  };

  // Handle contract update
  const handleEdit = async () => {
    try {
      // Create FormData object
      const formData = new FormData();
      
      // Convert editor state to HTML for saving
      const rawContentState = convertToRaw(editorState.getCurrentContent());
      const commentHtml = draftToHtml(rawContentState);
      
      // Add all fields to formData
      Object.keys(contractToEdit).forEach((key) => {
        if (contractToEdit[key] !== null && contractToEdit[key] !== undefined) {
          if (key === "file" && contractToEdit[key] instanceof File) {
            formData.append("file", contractToEdit[key]); // Append file field
          } else if (key !== "comment") { // Skip the comment field as we'll add it separately
            formData.append(key, contractToEdit[key]); // Append other fields
          }
        }
      });
      
      // Add the comment from the editor
      formData.append("comment", commentHtml);
  
      // Send PATCH request with FormData
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/contract/${contractToEdit._id}`,
        {
          method: "PATCH",
          headers: {
            "Authorization": `Bearer ${token}`,
          },
          body: formData,
        }
      );
      
      const responseData = await response.json();
  
      if (!response.ok) {
        toggleEditModal();
        setError(responseData.err || 'Failed to update contract');
      } else {
        setSuccess(`${toProperCase(documentType)} updated successfully!`);
        toggleEditModal();
        setRefreshData(prev => !prev); // Trigger a refresh
      }
    } catch (error) {
      console.error("Error updating contract:", error);
      setError("Error: " + error.message);
    }
  };

  // Confirm edit - Takes a single contract data and sets it to the contract to be edited
  const confirmEdit = (contract) => {
    const {_id, title, reference, country, contracting_party, document_type, status, expiry_date, notification_period, comment, contract_document} = contract;
    setContractToEdit({ 
      _id, 
      title, 
      reference, 
      country, 
      contracting_party, 
      document_type, 
      status, 
      expiry_date, 
      notification_period, 
      comment,
      contract_files: contract_document && contract_document.length > 0 ? contract_document[0].location : ''
    });
    
    // Reset editor state to empty when opening edit modal
    setEditorState(EditorState.createEmpty());
    
    toggleEditModal();
  };

  // Handle edit form input change
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setContractToEdit({ ...contractToEdit, [name]: value });
  };

  // Confirm deletion
  const confirmDelete = (contractId) => {
    setContractToDelete({ _id: contractId });
    toggleDeleteModal();
  };

  // Handle contract deletion
  const handleDelete = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/contract/${contractToDelete._id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) {
        let responseErr = await response.json();
        setError(responseErr.err || 'Failed to delete contract');
        console.log(`DELETE ERROR: ${JSON.stringify(responseErr)}`);
        toggleDeleteModal();
        return;
      }
      
      setSuccess(`${toProperCase(documentType)} deleted successfully!`);
      toggleDeleteModal();
      setRefreshData(prev => !prev); // Trigger a refresh
    } catch (error) {
      console.error(`Error deleting ${documentType}:`, error);
      setError("Error: " + error.message);
      toggleDeleteModal();
    }
  };

  // Handle search input change
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Filter contracts based on search term
  const filteredContracts = contracts
    .filter((contract) =>
      contract.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (contract.reference && contract.reference.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (contract.contracting_party && contract.contracting_party.toLowerCase().includes(searchTerm.toLowerCase()))
    );

  // Paginate contracts
  const indexOfLastContract = currentPage * contractsPerPage;
  const indexOfFirstContract = indexOfLastContract - contractsPerPage;
  const currentContracts = filteredContracts.slice(
    indexOfFirstContract,
    indexOfLastContract
  );

  // Paginate function
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Calculate total pages
  const totalPages = Math.ceil(filteredContracts.length / contractsPerPage);

  return (
    <div>
      {/* Manage Contracts Header */}
      <Card>
        <CardHeader>Manage {toProperCase(documentType)}s</CardHeader>
        <CardBody>
          {/* Search Bar */}
          <Row>
            <Col md={6}>
              <Input
                type="text"
                placeholder="Search by title, reference or contracting party..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </Col>
            <Col md={4}></Col>
            <Col md={2} className="float-right">
            {userData && userData.role !== 'director' && (
              <Button color="success" href={`/contracts/${documentType}/add?h=${isHome}`}> 
                Add {isHome ? "" : documentType}
              </Button>
            )}
            </Col>
          </Row><br/>

          {/* Alerts */}
          {error && <Alert color="danger">{error}</Alert>}
          {success && <Alert color="success">{success}</Alert>}
          
          {/* Contracts Table */}
          {loading ? (
            <div className="text-center my-3">
              <Spinner color="primary" />
            </div>
          ) : (
            <Table striped responsive>
              <thead>
                <tr>
                  <th>Created At</th>
                  <th>Title</th>
                  <th>Reference</th>
                  <th>Country</th>
                  <th>Contracting Party</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentContracts.length > 0 ? (
                  currentContracts.map((contract) => (
                    <tr key={contract._id}>
                      <td>{formatDate(contract.createdAt)}</td>
                      <td>{contract.title}</td>
                      <td>{contract.reference}</td>
                      <td>{contract.country}</td>
                      <td>{contract.contracting_party}</td>
                      <td style={{
                        color: contract.status === "expired" ? "red" : 
                               contract.status === "active" ? "green" : 
                               "inherit",
                        fontWeight: "600"
                      }}>
                        {contract.status}
                      </td>
                      <td>
                        <Button
                          color="outline-warning"
                          size="sm"
                          onClick={() => confirmView(contract._id)}
                        >
                          View
                        </Button>{" "}
                        {/* If the user is a director, they should not view the buttons below */}
                        {userData && userData.role !== "director" && (
                          <>
                            <Button 
                              color="outline-success" 
                              size="sm" 
                              onClick={() => confirmEdit(contract)}>
                              Edit
                            </Button>{" "}
                            {userData && userData.role === "IT" && (
                              <Button 
                                color="outline-danger" 
                                size="sm" 
                                onClick={() => confirmDelete(contract._id)}>
                                Delete
                              </Button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center">
                      No {documentType}s found matching your search criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Row>
              <Col className="d-flex justify-content-center align-items-center">
                <Button
                  color="secondary"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="me-2"
                >
                  Previous
                </Button>
                
                {/* Show limited page numbers with ellipsis for large page counts */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(pageNum => 
                    pageNum === 1 || 
                    pageNum === totalPages || 
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  )
                  .map((pageNum, index, array) => (
                    <React.Fragment key={pageNum}>
                      {index > 0 && array[index - 1] !== pageNum - 1 && (
                        <span className="mx-1">...</span>
                      )}
                      <Button
                        color={currentPage === pageNum ? "primary" : "light"}
                        onClick={() => paginate(pageNum)}
                        className="mx-1"
                      >
                        {pageNum}
                      </Button>
                    </React.Fragment>
                  ))}
                
                <Button
                  color="secondary"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ms-2"
                >
                  Next
                </Button>
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>

      {/* Add Contract Modal */}
      <Modal isOpen={modal} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Add {toProperCase(documentType)}</ModalHeader>
        <ModalBody>
          {/* Add Contract Form */}
          <Form>
            {/* Add fields for adding a new contract here */}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary">Save</Button>{" "}
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
        <ModalBody>Are you sure you want to delete this {documentType}?</ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleDelete}>
            Yes, Delete
          </Button>{" "}
          <Button color="secondary" onClick={toggleDeleteModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Contract Modal */}
      <Modal isOpen={editModal} toggle={toggleEditModal} size="lg">
        <ModalHeader toggle={toggleEditModal}>Edit {toProperCase(documentType)}</ModalHeader>
        <ModalBody>
          {contractToEdit && (
            <Form>
              {Object.keys(contractToEdit).map((key) => (
                !hiddenFields.includes(key) && (
                  <FormGroup key={key}>
                    <Label>{key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</Label>
                    
                    {/* Dropdown for status */}
                    {key === "status" ? (
                      <Input type="select" name={key} value={contractToEdit[key] || ''} onChange={handleEditChange}>
                        {statusOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Input>
                    ) : key === "document_type" ? (
                      // Dropdown for document type
                      <Input type="select" name={key} value={contractToEdit[key] || ''} onChange={handleEditChange}>
                        {documentOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Input>
                    ) : key === "country" ? (
                      // Dropdown for country
                      <Input type="select" name={key} value={contractToEdit[key] || ''} onChange={handleEditChange}>
                        {countryOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Input>
                    ) : key === "expiry_date" ? (
                      // Flatpickr for expiry_date
                      <Flatpickr
                        className="form-control d-block"
                        placeholder="dd MM yyyy"
                        options={{
                          altInput: true,
                          altFormat: "F j, Y", // Display format (e.g., January 1, 2023)
                          dateFormat: "Y-m-d", // Value format sent to the server
                        }}
                        name={key}
                        value={contractToEdit[key] || ''}
                        onChange={(date) => handleEditChange({ target: { name: key, value: date[0] } })}
                      />
                    ) : key === "contract_files" ? (
                      <>
                        {/* File input for contract file upload */}
                        <Input
                          type="file"
                          name={key}
                          accept=".pdf,.doc,.docx,.png,.jpg"
                          onChange={(e) =>
                            setContractToEdit({ ...contractToEdit, file: e.target.files[0] })
                          }
                        />
                        {contractToEdit[key] && (
                          <p style={{paddingTop: "5px"}}>
                            <em>Current file:</em> <span style={{color: "green"}}>
                              {contractToEdit[key].split('/').pop()}
                            </span>
                          </p>
                        )}
                      </>
                    ) : (
                      // Default input for other fields
                      <Input
                        type="text"
                        name={key}
                        value={contractToEdit[key] || ''}
                        onChange={handleEditChange}
                      />
                    )}
                  </FormGroup>
                )
              ))}
              
              {/* Comment Editor Section */}
              <FormGroup>
                <Label>Comments</Label>
                <CommentEditor 
                  editorState={editorState}
                  onEditorStateChange={handleEditorChange}
                />
              </FormGroup>
            </Form>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleEdit}>
            Save Changes
          </Button>{" "}
          <Button color="secondary" onClick={toggleEditModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Contracts;